import axios from './axios-with-authentication';
import * as backend from './backend-types';
import { AppError, ERROR_CODE, userMessages } from '../util/AppError';
import { DeviceType, PairingConfiguration, ScannerPostfix, SupportedDeviceType } from '../types';
import {
  API_BASE_PATH,
  DEFAULT_PATIENT_BARCODE_TYPE,
  VIQTOR_SERIAL_REGEX,
  VIQTOR_UDI_REGEX,
} from './constants';
import { resolveDeviceType } from './device-listing';
import { getCurrentUser } from './auth-user';
import { toAppError } from './error-mapping';
import {
  getPortalOrganizationId,
  getPortalPatientBarcodeFormat,
  getPortalPatientIdFormat,
  ScannedDeviceType,
  parseScannedDeviceCode,
} from './portal-adapter';
import { getFrontendDeviceBarcodeFormats } from './device-barcode-formats';
import { parseSupportedDeviceTypes, resolveRegexWithFallback } from './pairings.config';
import { buildClientUpdateRequest } from './pairings.client';
import {
  createHealthdotDevice,
  getDeviceStatusForPairing,
  getDevicesForPairing,
  PairingDevice,
} from './pairings.devices';
import {
  resolveAssignedClientIdForDevice,
  resolveAssignedDeviceIdForClient,
  resolvePortalPairingContext,
} from './pairings.context';
import { canAccessPairing } from '../auth/permissions';

export type ScannedPairingDeviceInfo = {
  deviceStatus?: backend.PortalDeviceStatus;
  batteryLevel?: number;
};

export type PatientPairingValidation = {
  alreadyHasDevice: boolean;
  assignedDeviceType?: DeviceType;
};

function normalizeDeviceReference(value?: string): string | undefined {
  const normalized = String(value || '').trim();
  return normalized ? normalized : undefined;
}

function clientHasWornViQtor(client: backend.PortalClientResponse): boolean {
  const serialNumber = normalizeDeviceReference(client.personalData?.device?.serialNumber);
  if (!serialNumber) return false;
  if (!VIQTOR_UDI_REGEX.test(serialNumber) && !VIQTOR_SERIAL_REGEX.test(serialNumber)) return false;
  return client.deviceStatus?.deviceStatus === 'OK';
}

function getPairingDeviceType(device?: PairingDevice, fallbackSerialNumber?: string): DeviceType {
  return resolveDeviceType(device, fallbackSerialNumber);
}

function resolveDeviceAssignedToClient(
  client: backend.PortalClientResponse,
  candidateDevices: PairingDevice[],
): PairingDevice | undefined {
  const assignedDeviceId = resolveAssignedDeviceIdForClient(client);
  const assignedDeviceSerialNumber = client.personalData?.device?.serialNumber;

  const assignedDevice = candidateDevices.find((candidateDevice) => {
    if (assignedDeviceId && candidateDevice.id === assignedDeviceId) return true;
    if (candidateDevice.clientId && candidateDevice.clientId === client.id) return true;
    return (
      assignedDeviceSerialNumber !== undefined &&
      candidateDevice.serialNumber.toLowerCase() === assignedDeviceSerialNumber.toLowerCase()
    );
  });

  return assignedDevice;
}

function getClientAssignedDeviceType(client: backend.PortalClientResponse): DeviceType | undefined {
  const assignedDeviceId = resolveAssignedDeviceIdForClient(client);
  const assignedDeviceSerialNumber = normalizeDeviceReference(
    client.personalData?.device?.serialNumber,
  );

  if (!assignedDeviceId && !assignedDeviceSerialNumber) return undefined;
  return getPairingDeviceType(undefined, assignedDeviceSerialNumber);
}

function resolveViQtorDeviceAssignedToClient(
  client: backend.PortalClientResponse,
  candidateDevices: PairingDevice[],
): PairingDevice | undefined {
  const assignedDevice = resolveDeviceAssignedToClient(client, candidateDevices);
  return getPairingDeviceType(assignedDevice, client.personalData?.device?.serialNumber) ===
    DeviceType.ViQtor
    ? assignedDevice
    : undefined;
}

function resolveHealthdotDeviceAssignedToClient(
  client: backend.PortalClientResponse,
  candidateDevices: PairingDevice[],
): PairingDevice | undefined {
  const assignedDevice = resolveDeviceAssignedToClient(client, candidateDevices);
  return getPairingDeviceType(assignedDevice, client.personalData?.device?.serialNumber) ===
    DeviceType.Healthdot
    ? assignedDevice
    : undefined;
}

function normalizeBarcodeType(value: unknown, fallback: string): string {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim().toUpperCase();
  return normalized ? normalized : fallback;
}

function normalizeBarcodeScannerPostfix(value: unknown, fallback: ScannerPostfix): ScannerPostfix {
  if (value === undefined) return fallback;
  if (value === null || value === '') return 'NONE';
  const normalized = String(value).trim().toUpperCase();
  if (normalized === 'NONE' || normalized === 'ENTER') {
    return normalized as ScannerPostfix;
  }
  return fallback;
}

export async function validatePatientExists(
  hospitalPatientId: string,
): Promise<PatientPairingValidation> {
  try {
    const { client } = await resolvePortalPairingContext(hospitalPatientId);
    const assignedDeviceType = getClientAssignedDeviceType(client);

    return {
      alreadyHasDevice: assignedDeviceType !== undefined || clientHasWornViQtor(client),
      ...(assignedDeviceType ? { assignedDeviceType } : {}),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err instanceof AppError) throw err;
    throw toAppError(err);
  }
}

/**
 * Creates the patient behind `hospitalPatientId` in the caller's organization.
 * The scanned ID is the only identifier the pairing flow has, and the backend
 * accepts it as one, so no further details are collected.
 */
export async function createPatientForPairing(hospitalPatientId: string): Promise<void> {
  try {
    const externalPatientId = String(hospitalPatientId || '').trim();
    const user = await getCurrentUser();
    const hcoId = getPortalOrganizationId(user);
    if (!hcoId) {
      throw new AppError(ERROR_CODE.ERR_BAD_ORGANIZATION_ID, userMessages[ERROR_CODE.ERR_UNKNOWN]);
    }

    const payload: backend.PortalClientCreateRequest = {
      healthCareOrganizationId: hcoId,
      externalPatientId,
    };
    await axios.post<backend.PortalClientResponse>(`${API_BASE_PATH}/clients`, payload);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err instanceof AppError) throw err;
    throw toAppError(err);
  }
}

export async function getPairingConfiguration(): Promise<PairingConfiguration> {
  try {
    let supportedDeviceTypes: SupportedDeviceType[] = [DeviceType.Healthdot, DeviceType.ViQtor];
    const fallbackPatientIdFormat = getPortalPatientIdFormat();
    const fallbackPatientBarcodeFormat = getPortalPatientBarcodeFormat();
    let patientIdFormat = fallbackPatientIdFormat;
    let patientIdBarcodeFormat = fallbackPatientBarcodeFormat;
    let barcodeType = DEFAULT_PATIENT_BARCODE_TYPE;
    let barcodeScannerPostfix: ScannerPostfix = 'ENTER';

    const user = await getCurrentUser();
    if (!canAccessPairing(user.userRole)) {
      throw new AppError(ERROR_CODE.ERR_NO_ACCESS, userMessages[ERROR_CODE.ERR_NO_ACCESS]);
    }

    const hcoId = getPortalOrganizationId(user);
    if (!hcoId) {
      throw new AppError(ERROR_CODE.ERR_BAD_ORGANIZATION_ID, userMessages[ERROR_CODE.ERR_UNKNOWN]);
    }

    supportedDeviceTypes = parseSupportedDeviceTypes(
      user.healthCareOrganization?.supportedDeviceTypes || [],
    );
    const userHco = user.healthCareOrganization;
    patientIdFormat = resolveRegexWithFallback(userHco?.patientIdFormat, patientIdFormat);
    patientIdBarcodeFormat = resolveRegexWithFallback(
      userHco?.patientIdBarcodeFormat,
      patientIdBarcodeFormat,
    );
    const userBarcodeType = userHco?.patientIdBarcodeType;
    const userBarcodeScannerPostfix = userHco?.barcodeScannerPostfix;
    barcodeType = normalizeBarcodeType(userBarcodeType, barcodeType);
    barcodeScannerPostfix = normalizeBarcodeScannerPostfix(
      userBarcodeScannerPostfix,
      barcodeScannerPostfix,
    );

    try {
      // Prefer HCO-specific values when available, keep auth/me-derived values as safe defaults.
      const { data: hco } = await axios.get<backend.PortalHealthCareOrganizationResponse>(
        `${API_BASE_PATH}/hcos/${hcoId}`,
      );
      if (Array.isArray(hco.supportedDeviceTypes) && hco.supportedDeviceTypes.length > 0) {
        supportedDeviceTypes = parseSupportedDeviceTypes(hco.supportedDeviceTypes);
      }
      patientIdFormat = resolveRegexWithFallback(hco.patientIdFormat, patientIdFormat);
      patientIdBarcodeFormat = resolveRegexWithFallback(
        hco.patientIdBarcodeFormat,
        patientIdBarcodeFormat,
      );
      barcodeType = normalizeBarcodeType(hco.patientIdBarcodeType, barcodeType);
      barcodeScannerPostfix = normalizeBarcodeScannerPostfix(
        hco.barcodeScannerPostfix,
        barcodeScannerPostfix,
      );
    } catch {
      // keep compatibility when this endpoint/field is not available yet in some environments.
    }

    return {
      patientIdFormat,
      // Default patient camera scan accepts all barcode formats unless backend narrows it down.
      barcodeType,
      patientIdBarcodeFormat,
      barcodeScannerPostfix,
      deviceBarcodeFormats: getFrontendDeviceBarcodeFormats(supportedDeviceTypes),
      supportedDeviceTypes,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err instanceof AppError) throw err;
    throw toAppError(err);
  }
}

export async function getScannedDeviceInfo(
  scannedDeviceCode: string,
  deviceTypeHint?: DeviceType,
): Promise<ScannedPairingDeviceInfo | undefined> {
  try {
    const parsedDevice = parseScannedDeviceCode(scannedDeviceCode);
    const scannedDeviceType: ScannedDeviceType =
      deviceTypeHint === DeviceType.Healthdot || deviceTypeHint === DeviceType.ViQtor
        ? deviceTypeHint
        : parsedDevice.deviceType;

    // Battery warning is only relevant for viQtor in pairing flow.
    if (scannedDeviceType !== DeviceType.ViQtor) return undefined;

    const user = await getCurrentUser();
    const hcoId = getPortalOrganizationId(user);
    if (!hcoId) {
      throw new AppError(ERROR_CODE.ERR_BAD_ORGANIZATION_ID, userMessages[ERROR_CODE.ERR_UNKNOWN]);
    }

    const candidateDevices = await getDevicesForPairing(hcoId);
    const matchedDevice = candidateDevices.find(
      (entry) => entry.serialNumber.toLowerCase() === parsedDevice.serialNumber.toLowerCase(),
    );

    if (!matchedDevice) {
      throw new AppError(
        ERROR_CODE.ERR_DEVICE_NOT_FOUND,
        userMessages[ERROR_CODE.ERR_DEVICE_NOT_FOUND],
      );
    }

    try {
      // Prefer the dedicated device-adapter status endpoint when available.
      return await getDeviceStatusForPairing(matchedDevice.id);
    } catch {
      // Fall back to the bulk portal device payload.
    }

    return {
      deviceStatus: matchedDevice.deviceStatus,
      batteryLevel: matchedDevice.batteryLevel,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err instanceof AppError) throw err;
    throw toAppError(err);
  }
}

export async function getScannedDeviceStatus(
  scannedDeviceCode: string,
  deviceTypeHint?: DeviceType,
): Promise<backend.PortalDeviceStatus | undefined> {
  const deviceInfo = await getScannedDeviceInfo(scannedDeviceCode, deviceTypeHint);
  return deviceInfo?.deviceStatus;
}

export async function createPairing(
  hospitalPatientId: string,
  healthdotId: string,
  clinicalUnitId?: string | null,
  cohortId?: string | null,
  deviceTypeHint?: DeviceType,
): Promise<void> {
  try {
    const parsedDevice = parseScannedDeviceCode(healthdotId);
    if (
      deviceTypeHint === DeviceType.Healthdot &&
      parsedDevice.deviceType !== DeviceType.Healthdot
    ) {
      throw new AppError(ERROR_CODE.ERR_BAD_BARCODE, userMessages[ERROR_CODE.ERR_BAD_BARCODE]);
    }
    // A recognised HealthDot whose serial cannot be read stops here, before any call goes
    // out: further down an unknown HealthDot serial is created in the device-adapter, and
    // an unreadable one would register a device nobody can pair.
    if (parsedDevice.format === 'HD5' && !parsedDevice.valid) {
      throw new AppError(ERROR_CODE.ERR_BAD_BARCODE, userMessages[ERROR_CODE.ERR_BAD_BARCODE]);
    }
    const scannedDeviceType: ScannedDeviceType =
      deviceTypeHint === DeviceType.Healthdot || deviceTypeHint === DeviceType.ViQtor
        ? deviceTypeHint
        : parsedDevice.deviceType;
    const scannedDevice = {
      ...parsedDevice,
      deviceType: scannedDeviceType,
    };
    const normalizedClinicalUnitId = clinicalUnitId;
    const normalizedCohortId = cohortId;

    const { hcoId, client, clients } = await resolvePortalPairingContext(hospitalPatientId);

    const candidateDevices = await getDevicesForPairing(hcoId);
    let device = candidateDevices.find(
      (entry) => entry.serialNumber.toLowerCase() === scannedDevice.serialNumber.toLowerCase(),
    );

    if (!device && scannedDevice.deviceType === DeviceType.Healthdot) {
      if (scannedDevice.format !== 'HD5') {
        throw new AppError(ERROR_CODE.ERR_BAD_BARCODE, userMessages[ERROR_CODE.ERR_BAD_BARCODE]);
      }
      device = await createHealthdotDevice(scannedDevice.serialNumber, hcoId);
    }

    if (!device) {
      throw new AppError(
        ERROR_CODE.ERR_DEVICE_NOT_FOUND,
        userMessages[ERROR_CODE.ERR_DEVICE_NOT_FOUND],
      );
    }

    if (scannedDevice.deviceType === DeviceType.Healthdot) {
      // The scanned device is a Healthdot. What happens to the device the patient already
      // wears depends on what that device is.
      const patientHealthdot = resolveHealthdotDeviceAssignedToClient(client, candidateDevices);
      if (patientHealthdot && patientHealthdot.id === device.id) {
        // The patient already wears the scanned Healthdot: nothing to do.
        return;
      }
      // The patient wears another Healthdot: it is not released here. The platform swaps it
      // for the scanned one itself, and only once the scanned one is known to be assignable.
      // Releasing it first would leave the patient with none whenever the scanned one is
      // refused, and a provisioned Healthdot that is released is spent.
      //
      // The patient wears a viQtor: the platform does not allow a patient to be paired to a
      // viQtor and a Healthdot at once, and swaps only a Healthdot for a Healthdot. The app
      // therefore releases the patient's viQtor before assigning the scanned Healthdot: the
      // device paired last wins, so a nurse replacing a failing device in the field is not
      // blocked by the one it replaces.
      const patientViQtor = resolveViQtorDeviceAssignedToClient(client, candidateDevices);
      if (patientViQtor) {
        // Not caught: if the patient's viQtor could not be released, the assignment below
        // would be refused, and that refusal would read as a problem with the scanned Healthdot.
        await axios.delete(
          `${API_BASE_PATH}/clients/${client.id}/device-assignment/${patientViQtor.id}`,
        );
      }
    }

    // Persist the selected clinical unit/cohort on the client before device assignment.
    const shouldUpdateClient =
      normalizedClinicalUnitId !== undefined || normalizedCohortId !== undefined;

    if (shouldUpdateClient) {
      const updatePayload = buildClientUpdateRequest(
        client,
        normalizedClinicalUnitId,
        normalizedCohortId,
      );
      await axios.put(`${API_BASE_PATH}/clients/${client.id}`, updatePayload);
    }

    if (scannedDevice.deviceType === DeviceType.ViQtor) {
      const detachedAssignments = new Set<string>();
      const detachViQtorAssignment = async (
        clientId: string | undefined,
        deviceId: string,
      ): Promise<void> => {
        if (!clientId) return;
        const detachKey = `${clientId}:${deviceId}`;
        if (detachedAssignments.has(detachKey)) return;

        detachedAssignments.add(detachKey);
        await axios.delete(`${API_BASE_PATH}/clients/${clientId}/device-assignment/${deviceId}`);
      };

      // viQtor can be re-assigned: detach from previous client first when needed.
      // Backend may not expose `clientId` on /devices consistently, so fallback
      // to /clients mapping via personalData.device.id.
      const currentClientId =
        (device.clientId || '').trim() || resolveAssignedClientIdForDevice(clients, device.id);

      await detachViQtorAssignment(currentClientId, device.id);

      const currentTargetDevice = resolveViQtorDeviceAssignedToClient(client, candidateDevices);
      if (currentTargetDevice && currentTargetDevice.id !== device.id) {
        await detachViQtorAssignment(client.id, currentTargetDevice.id);
      }
    }

    const assignmentUrl = `${API_BASE_PATH}/clients/${client.id}/device-assignment/${device.id}`;
    await axios.put(assignmentUrl);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err instanceof AppError) throw err;
    throw toAppError(err);
  }
}
